// Project Management Types

// =============================================================================
// Enums / Union Types
// =============================================================================

export type ProjectType = 'internal_rd' | 'customer_originated' | 'software';
export type ProjectStatus = 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
export type ProjectSourceType = 'none' | 'quotation' | 'sales_order';
export type PhaseType = 'concept' | 'feasibility' | 'pre_bom' | 'evt' | 'dvt' | 'pvt' | 'pp' | 'mp' | 'sustaining' | 'custom' | 'architecture' | 'sprint_planning' | 'development' | 'testing' | 'staging' | 'release' | 'maintenance';
export type PhaseStatus = 'not_started' | 'in_progress' | 'review' | 'completed';
export type TaskStatus = 'plan' | 'doing' | 'review' | 'done';
export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type ProjectViewMode = 'list' | 'detail' | 'gantt';

// =============================================================================
// Status Labels & Colors (for UI)
// =============================================================================

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: 'Planning',
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const PROJECT_STATUS_COLORS: Record<ProjectStatus, string> = {
  planning: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  on_hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export const PHASE_STATUS_LABELS: Record<PhaseStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  review: 'Review',
  completed: 'Completed',
};

export const PHASE_STATUS_COLORS: Record<PhaseStatus, string> = {
  not_started: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  plan: 'Plan',
  doing: 'Doing',
  review: 'Review',
  done: 'Done',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  plan: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  doing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  done: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
};

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const PRIORITY_COLORS: Record<Priority, string> = {
  low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300',
};

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  internal_rd: 'Internal R&D',
  customer_originated: 'Customer Originated',
  software: 'Software',
};

export const PHASE_TYPE_LABELS: Record<PhaseType, string> = {
  concept: 'Concept',
  feasibility: 'Feasibility',
  pre_bom: 'PRE-BOM',
  evt: 'EVT',
  dvt: 'DVT',
  pvt: 'PVT',
  pp: 'PP',
  mp: 'MP',
  sustaining: 'Sustaining',
  custom: 'Custom',
  architecture: 'Architecture',
  sprint_planning: 'Sprint Planning',
  development: 'Development',
  testing: 'Testing/QA',
  staging: 'Staging',
  release: 'Release',
  maintenance: 'Maintenance',
};

// Phase background colors for visual distinction
export const PHASE_TYPE_COLORS: Record<PhaseType, { bg: string; border: string; light: string; dark: string }> = {
  concept: {
    bg: 'bg-slate-100 dark:bg-slate-800/50',
    border: 'border-slate-300 dark:border-slate-600',
    light: '#e2e8f0',  // slate-200
    dark: '#475569',   // slate-600
  },
  feasibility: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    border: 'border-purple-300 dark:border-purple-700',
    light: '#e9d5ff',  // purple-200
    dark: '#7e22ce',   // purple-700
  },
  pre_bom: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    border: 'border-indigo-300 dark:border-indigo-700',
    light: '#c7d2fe',  // indigo-200
    dark: '#4338ca',   // indigo-700
  },
  evt: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    border: 'border-blue-300 dark:border-blue-700',
    light: '#bfdbfe',  // blue-200
    dark: '#1d4ed8',   // blue-700
  },
  dvt: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    border: 'border-cyan-300 dark:border-cyan-700',
    light: '#a5f3fc',  // cyan-200
    dark: '#0e7490',   // cyan-700
  },
  pvt: {
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    border: 'border-teal-300 dark:border-teal-700',
    light: '#99f6e4',  // teal-200
    dark: '#0f766e',   // teal-700
  },
  pp: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    border: 'border-amber-300 dark:border-amber-700',
    light: '#fde68a',  // amber-200
    dark: '#b45309',   // amber-700
  },
  mp: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    border: 'border-emerald-300 dark:border-emerald-700',
    light: '#a7f3d0',  // emerald-200
    dark: '#047857',   // emerald-700
  },
  sustaining: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    border: 'border-orange-300 dark:border-orange-700',
    light: '#fed7aa',  // orange-200
    dark: '#c2410c',   // orange-700
  },
  custom: {
    bg: 'bg-gray-100 dark:bg-gray-800/50',
    border: 'border-gray-300 dark:border-gray-600',
    light: '#e5e7eb',  // gray-200
    dark: '#4b5563',   // gray-600
  },
  architecture: {
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    border: 'border-rose-300 dark:border-rose-700',
    light: '#fecdd3',  // rose-200
    dark: '#be123c',   // rose-700
  },
  sprint_planning: {
    bg: 'bg-lime-100 dark:bg-lime-900/30',
    border: 'border-lime-300 dark:border-lime-700',
    light: '#d9f99d',  // lime-200
    dark: '#4d7c0f',   // lime-700
  },
  development: {
    bg: 'bg-sky-100 dark:bg-sky-900/30',
    border: 'border-sky-300 dark:border-sky-700',
    light: '#bae6fd',  // sky-200
    dark: '#0369a1',   // sky-700
  },
  testing: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    border: 'border-violet-300 dark:border-violet-700',
    light: '#ddd6fe',  // violet-200
    dark: '#6d28d9',   // violet-700
  },
  staging: {
    bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',
    border: 'border-fuchsia-300 dark:border-fuchsia-700',
    light: '#f5d0fe',  // fuchsia-200
    dark: '#a21caf',   // fuchsia-700
  },
  release: {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    border: 'border-pink-300 dark:border-pink-700',
    light: '#fbcfe8',  // pink-200
    dark: '#be185d',   // pink-700
  },
  maintenance: {
    bg: 'bg-stone-100 dark:bg-stone-900/30',
    border: 'border-stone-300 dark:border-stone-700',
    light: '#e7e5e4',  // stone-200
    dark: '#44403c',   // stone-700
  },
};

// =============================================================================
// Project Templates
// =============================================================================

export const INTERNAL_RD_PHASES: PhaseType[] = [
  'concept',
  'feasibility',
  'pre_bom',
  'evt',
  'dvt',
  'pvt',
  'mp',
  'sustaining',
];

export const CUSTOMER_ORIGINATED_PHASES: PhaseType[] = [
  'concept',
  'pre_bom',
  'evt',
  'dvt',
  'pvt',
  'pp',
  'mp',
];

export const SOFTWARE_PHASES: PhaseType[] = [
  'concept',
  'architecture',
  'sprint_planning',
  'development',
  'testing',
  'staging',
  'release',
  'maintenance',
];

// =============================================================================
// Core Interfaces
// =============================================================================

export interface IProject {
  id: string;
  project_code: string;
  project_name: string;
  product_id?: string;
  project_type: ProjectType;
  source_type: ProjectSourceType;
  source_id?: string;
  customer_code?: string;
  status: ProjectStatus;
  priority: Priority;
  project_manager: string;
  start_date: string;
  target_end_date: string;
  actual_end_date?: string;
  budget?: number;
  currency: string;
  progress: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface IProjectPhase {
  id: string;
  project_id: string;
  phase_code: string;
  phase_name: string;
  phase_type: PhaseType;
  status: PhaseStatus;
  sort_order: number;
  planned_start: string;
  planned_end: string;
  actual_start?: string;
  actual_end?: string;
  owner: string;
  progress: number;
  deliverables?: string[];
  created_at: string;
  updated_at: string;
}

export interface IProjectTask {
  id: string;
  project_id: string;
  phase_id: string;
  task_code: string;
  task_name: string;
  task_owner: string;
  status: TaskStatus;
  priority: Priority;
  duration: number; // hours
  planned_start: string;
  planned_end: string;
  actual_start?: string;
  actual_end?: string;
  prior_deps: string[]; // array of task IDs
  sort_order: number;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Request/Response Types
// =============================================================================

// Phase definition for project creation (customer-originated projects)
export interface IPhaseDefinition {
  phase_name: string;
  phase_type: PhaseType;
}

// Predefined phases available for customer-originated projects
export const PREDEFINED_PHASES: IPhaseDefinition[] = [
  { phase_name: 'Requirements', phase_type: 'concept' },
  { phase_name: 'Design', phase_type: 'concept' },
  { phase_name: 'PRE-BOM', phase_type: 'pre_bom' },
  { phase_name: 'EVT', phase_type: 'evt' },
  { phase_name: 'DVT', phase_type: 'dvt' },
  { phase_name: 'PVT', phase_type: 'pvt' },
  { phase_name: 'Pilot Run', phase_type: 'pp' },
  { phase_name: 'Mass Production', phase_type: 'mp' },
];

export interface IProjectCreateRequest {
  project_name: string;
  product_id?: string;
  project_type: ProjectType;
  source_type?: ProjectSourceType;
  source_id?: string;
  customer_code?: string;
  priority?: Priority;
  project_manager: string;
  start_date: string;
  target_end_date: string;
  budget?: number;
  currency?: string;
  description?: string;
  // For customer-originated projects: selected phases to create
  phases?: IPhaseDefinition[];
}

export interface IProjectUpdateRequest {
  project_name?: string;
  product_id?: string;
  status?: ProjectStatus;
  priority?: Priority;
  project_manager?: string;
  start_date?: string;
  target_end_date?: string;
  actual_end_date?: string;
  budget?: number;
  currency?: string;
  progress?: number;
  description?: string;
}

export interface IPhaseCreateRequest {
  phase_name: string;
  phase_type: PhaseType;
  planned_start: string;
  planned_end: string;
  owner: string;
  deliverables?: string[];
}

export interface IPhaseUpdateRequest {
  phase_name?: string;
  status?: PhaseStatus;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  owner?: string;
  progress?: number;
  deliverables?: string[];
}

export interface ITaskCreateRequest {
  phase_id: string;
  task_name: string;
  task_owner: string;
  priority?: Priority;
  duration: number;
  planned_start: string;
  planned_end: string;
  prior_deps?: string[];
  tags?: string[];
}

export interface ITaskUpdateRequest {
  task_name?: string;
  task_owner?: string;
  status?: TaskStatus;
  priority?: Priority;
  duration?: number;
  planned_start?: string;
  planned_end?: string;
  actual_start?: string;
  actual_end?: string;
  prior_deps?: string[];
  tags?: string[];
}

export interface ITaskMoveRequest {
  status: TaskStatus;
  phase_id?: string;
  sort_order?: number;
}

export interface IReorderRequest {
  orders: Array<{ id: string; sort_order: number }>;
}

// =============================================================================
// API Response Types
// =============================================================================

export interface IProjectListResponse {
  projects: IProject[];
  total_count: number;
}

export interface IProjectDetailResponse {
  project: IProject;
  phases: IProjectPhase[];
  tasks: IProjectTask[];
}

export interface IPhaseListResponse {
  phases: IProjectPhase[];
}

export interface ITaskListResponse {
  tasks: IProjectTask[];
}

export interface IDependencyCheckResponse {
  ok: boolean;
  incomplete_tasks: string[]; // task names that are not done
}

// =============================================================================
// Gantt Chart Types
// =============================================================================

export type GanttItemType = 'project' | 'phase' | 'task' | 'milestone';

export interface IGanttItem {
  id: string;
  name: string;
  type: GanttItemType;
  start: Date;
  end: Date;
  progress: number;
  dependencies?: string[];
  color?: string;
  collapsed?: boolean;
  parentId?: string;
  level: number; // 0 = project, 1 = phase, 2 = task
}

export interface IGanttViewState {
  zoomLevel: 'day' | 'week' | 'month';
  showToday: boolean;
  collapsedPhases: Set<string>;
}

// =============================================================================
// Flow/DAG Types (for React Flow)
// =============================================================================

export interface IPhaseNodeData {
  phase: IProjectPhase;
  tasks: IProjectTask[];
  onEdit: (phase: IProjectPhase) => void;
  onExpand: (phaseId: string) => void;
  expanded: boolean;
}

export interface ITaskNodeData {
  task: IProjectTask;
  onEdit: (task: IProjectTask) => void;
}

// =============================================================================
// Customer Reference (from existing customer service)
// =============================================================================

export interface ICustomerRef {
  customer_code: string;
  chinese_name: string;
  phone?: string;
  email?: string;
}
